import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaHeart
} from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f1a 100%);
  color: white;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxxl} ${props => props.theme.spacing.lg};
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.md};
  }
`;

const FooterSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const Logo = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const LogoText = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.sm};
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const LogoTagline = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FooterDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ContactItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1rem;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const FooterLink = styled(motion.a)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(5px);
  }
`;

const SectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: white;
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing.xl} 0;
  text-align: center;
`;

const CopyrightText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const Footer = ({ setCursorVariant }) => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Servizi', path: '/servizi' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contatti', path: '/contatti' }
  ];

  const services = [
    'Sviluppo Web',
    'App Mobile', 
    'Realtà Virtuale',
    'Intelligenza Artificiale',
    'Soluzioni Cloud',
    'Cybersecurity'
  ];

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <Logo>
            <LogoText>D-<span>Soft</span></LogoText>
            <LogoTagline>Innovazione. Qualità. Risultati.</LogoTagline>
          </Logo>
          
          <FooterDescription>
            Trasformiamo idee in soluzioni digitali innovative. 
            Con passione e competenza, aiutiamo le aziende a crescere 
            nell'era digitale attraverso tecnologie all'avanguardia.
          </FooterDescription>
          
          <ContactList>
            <ContactItem>
              <FaEnvelope />
              <span>commerciale@d-soft.it</span>
            </ContactItem>
            <ContactItem>
              <FaPhone />
              <span>+39 3791408587</span>
            </ContactItem>
            <ContactItem>
              <FaMapMarkerAlt />
              <span>Via Vincenzo Foppa 55, Milano</span>
            </ContactItem>
          </ContactList>
        </FooterSection>

        <FooterSection>
          <SectionTitle>Link Rapidi</SectionTitle>
          <FooterLinks>
            {quickLinks.map((link, index) => (
              <FooterLink
                key={index}
                as={Link}
                to={link.path}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                {link.name}
              </FooterLink>
            ))}
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <SectionTitle>I Nostri Servizi</SectionTitle>
          <FooterLinks>
            {services.map((service, index) => (
              <FooterLink
                key={index}
                as={Link}
                to="/servizi"
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                {service}
              </FooterLink>
            ))}
          </FooterLinks>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <div style={{ maxWidth: '1120px', margin: '0 auto', padding: '0 24px' }}>
          <CopyrightText>
            © 2024 D-Soft. Tutti i diritti riservati. | P.IVA: 10935730969
          </CopyrightText>
        </div>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer; 